import { useNavigate } from 'react-router-dom';
import useQueryValues from '@/hooks/useQueryValues';
import usePaginationQuery from '@/hooks/usePaginationQuery';
import { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useQuery } from '@tanstack/react-query';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import { NAV_CONST } from '@/src/constants/nav';
import Filter from '@/components/organism/Filter';
import SectionCardNoneTitle from '@/components/molecule/SectionCardNoneTitle';
import Typo from '@/components/atom/Typo';
import PATH from '@/src/routes/path';
import Table from '@/components/atom/Table';
import {
  getRecyclingRequestsAPI,
  getRecyclingRequestsExcelAPI,
  getRecyclingStatusRefreshAPI,
} from '@/src/apis/rest/recyclingRequests';
import { recyclingRequestFilters } from '@/src/pages/RecyclingRequests/filter';
import { recyclingRequestListColumn } from '@/src/pages/RecyclingRequests/column';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';
import Button from '@/components/atom/Button';
import { useToast } from '@/hooks/useToast';

function RecyclingRequestsListPage() {
  const navigate = useNavigate();

  const { defaultValues: defaultSearchParams } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();
  const { toast } = useToast();

  const gridRef = useRef<AgGridReact>(null);
  const [searchParams, setSearchParams] = useState(defaultSearchParams);

  const {
    data: recyclingRequestsListData,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['recycling-requests', searchParams, page, pageSize],
    queryFn: () => {
      return getRecyclingRequestsAPI({
        ...searchParams,
        page,
        pageSize,
      });
    },
  });

  const {
    data: refreshData,
    isFetching: isRefreshFetching,
    refetch: refreshRecyclingStatus,
    isLoading: isRefreshLoading,
    isSuccess: isRefreshSuccess,
    isError: isRefreshError,
    isRefetching,
  } = useQuery({
    queryKey: ['recycling-requests', 'refresh'],
    staleTime: 0,
    queryFn: () => {
      return getRecyclingStatusRefreshAPI();
    },
    enabled: false,
  });

  const handleRefreshClick = async () => {
    if (isRefreshLoading || isRefreshFetching) return;
    await refreshRecyclingStatus();
  };

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  useEffect(() => {
    if (isRefetching) {
      return;
    }

    if (isRefreshSuccess) {
      toast({
        variant: 'success',
        title: '성공',
        description: `${refreshData?.totalCnt || 0}건의 수거 상태가 갱신되었습니다.`,
      });
    }
    if (isRefreshError) {
      toast({
        variant: 'error',
        title: '실패',
        description: '수거 상태 갱신에 실패했습니다.',
      });
    }
  }, [
    isRefreshSuccess,
    isRefreshError,
    toast,
    refreshData?.totalCnt,
    isRefetching,
  ]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.recyclingRequests}
        breadcrumbList={[NAV_CONST.recyclingRequests]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={recyclingRequestFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setSearchParams(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">수거 신청 목록</Typo>
              <div className="flex items-center gap-[8px]">
                <ExcelDownloadButton
                  disabled={
                    isError ||
                    isFetching ||
                    !recyclingRequestsListData?.rows?.length
                  }
                  fileName="수거_신청_리스트"
                  downloadExcelAPI={getRecyclingRequestsExcelAPI}
                  searchParams={searchParams}
                />
                <Button
                  buttonSize="medium"
                  buttonColor="secondary"
                  onClick={() => handleRefreshClick()}
                >
                  <div className="flex items-center gap-x-2">
                    택배 정보 최신화 (ALPS)
                  </div>
                </Button>
              </div>
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : recyclingRequestsListData?.rows || []}
              isPaginationDataMapping
              columnDefs={recyclingRequestListColumn(e => {
                navigate(
                  PATH.RECYCLING_REQUESTS_DETAIL.replace(':id', String(e)),
                );
              })}
              total={recyclingRequestsListData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>
    </>
  );
}

export default RecyclingRequestsListPage;
